/**
 * The Basic Registration form used by the end-user to create a user account
 */
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';

import {
  P,
  FormInputText,
  FormInputPassword,
  Button,
  Icon,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import AccessTokenForm from './AccessTokenFormV2.tsx';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import { LoginLayout } from '../Login/LoginLayout.tsx';
import UserAPI from '../../Lib/Api/user';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_SCRIPT_URL = `https://www.recaptcha.net/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

const VALIDATION_MESSAGES = {
  required: (field) => `Please enter your ${field}.`,
  passwordMinLength: 'Your password must be at least 6 characters.',
  passwordsMismatch: 'Passwords do not match.',
};

const FIELD_NAMES = {
  first_name: 'first name',
  last_name: 'last name',
  email: 'email',
  password: 'password',
  confirmPassword: 'password confirmation',
};

function getErrorDetails(error, matchingPasswords) {
  if (!matchingPasswords) {
    return {
      title: 'Passwords Do Not Match',
      description:
        'The passwords you entered do not match. Please make sure both passwords are identical and try again.',
    };
  }
  if (error) {
    return {
      title: error,
      description: null,
    };
  }
  return {
    title: null,
    description: null,
  };
}

function validateField(fieldName, value) {
  if (fieldName === 'email') {
    return null;
  }

  if (!value.length) {
    if (fieldName === 'password' || fieldName === 'confirmPassword') {
      return VALIDATION_MESSAGES.required('new password');
    }
    return VALIDATION_MESSAGES.required(FIELD_NAMES[fieldName]);
  }

  if (
    (fieldName === 'password' || fieldName === 'confirmPassword') &&
    value.length < 6
  ) {
    return VALIDATION_MESSAGES.passwordMinLength;
  }

  return '';
}

function BasicRegistrationForm(props) {
  const {
    isLoggedIn = false,
    influencer = false,
    location = {},
    validAccessToken = false,
    verifyInviteToken,
    loading,
    error = null,
    token = null,
  } = props;

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [matchingPasswords, setMatchingPasswords] = useState(true);
  const [errors, setErrors] = useState({});
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [isRecaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('REACT_APP_RECAPTCHA_SITE_KEY is not set');
      return undefined;
    }

    const script = document.createElement('script');
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaReady(true);
      });
    };
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
      setRecaptchaReady(true);
      setRecaptchaError('Recaptcha is unavailable.');
    };
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector(
        `script[src="${RECAPTCHA_SCRIPT_URL}"]`,
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (token) {
      setForm((prevForm) => ({
        ...prevForm,
        email: token.email || '',
        vendor_name: token.company_name || '',
      }));
    }
  }, [token]);

  const validateRecaptcha = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY) {
      return { success: true };
    }

    if (!window.grecaptcha) {
      return { success: false, error: 'reCAPTCHA failed to load' };
    }

    try {
      const recaptchaToken = await UserAPI.validateRecaptcha();
      const response = await UserAPI.validateToken(recaptchaToken);
      return { success: get(response, 'data.success', false) };
    } catch (err) {
      console.error('reCAPTCHA validation error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleBlur = (fieldName, value) => {
    const errorMessage = validateField(fieldName, value);
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: errorMessage }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDisableSubmit(true);
    setRecaptchaError(null);

    let hasErrors = false;
    const newErrors = {};

    Object.entries(form).forEach(([fieldName, value]) => {
      const errorMessage = validateField(fieldName, value);
      if (errorMessage) {
        hasErrors = true;
        newErrors[fieldName] = errorMessage;
      }
    });

    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = VALIDATION_MESSAGES.passwordsMismatch;
      hasErrors = true;
      setMatchingPasswords(false);
    } else {
      setMatchingPasswords(true);
    }

    setErrors(newErrors);

    if (hasErrors) {
      setDisableSubmit(false);
      return;
    }

    const recaptchaResult = await validateRecaptcha();
    if (!recaptchaResult.success) {
      setRecaptchaError('reCAPTCHA verification failed. Please try again.');
      setDisableSubmit(false);
      return;
    }

    const { signup } = props;
    signup({
      ...form,
      invited_by: get(props, 'token.requesting_party_id'),
      invite_token: get(props, 'token.token'),
    })
      .then(() => {})
      .catch(console.error)
      .finally(() => setDisableSubmit(false));
  };

  if (isLoggedIn) {
    return <Redirect to={{ pathname: '/', state: { from: location } }} />;
  }

  const params = decodeLocation(location.search);

  if (!validAccessToken) {
    return (
      <AccessTokenForm
        loading={loading}
        onSubmit={verifyInviteToken}
        error={error}
        token={params.token}
        influencer={influencer}
      />
    );
  }

  return (
    <LoginLayout
      isInfluencer={influencer}
      errorTitle={getErrorDetails(error, matchingPasswords).title}
      errorDescription={getErrorDetails(error, matchingPasswords).description}
    >
      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <Stack direction="col" gap="md">
          <Stack direction="col" gap="sm" className="w-[376px] h-[56px] pb-1">
            <P size="lg" weight="medium">
              Create your profile
            </P>
            <P size="sm" color="disabled">
              {form.email}
            </P>
          </Stack>
          <Stack direction="row" className="space-x-2">
            <FormInputText
              name="first_name"
              label="First Name"
              value={form.first_name}
              onChange={handleChange}
              error={errors.first_name}
              onBlurValue={(value) => handleBlur('first_name', value)}
            />
            <FormInputText
              name="last_name"
              label="Last Name"
              value={form.last_name}
              onChange={handleChange}
              error={errors.last_name}
              onBlurValue={(value) => handleBlur('last_name', value)}
            />
          </Stack>
          <FormInputPassword
            label="Password"
            name="password"
            value={form.password}
            onChange={handleChange}
            error={errors.password}
            onBlurValue={(value) => handleBlur('password', value)}
          />
          <FormInputPassword
            label="Confirm Password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
            onBlurValue={(value) => handleBlur('confirmPassword', value)}
          />
          {recaptchaError && (
            <P size="sm" className="text-danger-700">
              {recaptchaError}
            </P>
          )}
          <Stack direction="col" gap="md" className="mt-5">
            <Button
              className="w-full"
              type="submit"
              disabled={
                disableSubmit || (!isRecaptchaReady && !!RECAPTCHA_SITE_KEY)
              }
              loading={loading}
            >
              Continue
              <Icon.ArrowRight />
            </Button>
          </Stack>
          <P size="2xs" color="disabled">
            {`By utilizing this portal, I agree to Fashion Nova's Privacy Policy.`}
          </P>
        </Stack>
      </form>
    </LoginLayout>
  );
}

BasicRegistrationForm.propTypes = {
  signup: PropTypes.func.isRequired,
  verifyInviteToken: PropTypes.func.isRequired,
  influencer: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  validAccessToken: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  token: PropTypes.shape({
    email: PropTypes.string,
    company_name: PropTypes.string,
  }),
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
    state: PropTypes.shape({}),
  }),
};

export default BasicRegistrationForm;
